import * as GameplayHelpers from 'helpers/gameplay';
import {
  RED_PIECES,
  BLUE_PIECES,
  PIECE_TYPES,
  ITEMS,
  LOGIC_DOOR,
  UNWALKABLE,
  WALKABLE,
  SHADOW,
  WARP,
  KEY,
  LOCKED_DOOR
} from 'helpers/constants';
import { loadProgress } from 'helpers/player';
import { rand } from 'helpers/sharedHelpers';

export function tileInRect(tile, rect) {
  const left = tile.x >= rect.x;
  const top = tile.y >= rect.y;
  const right = tile.x <= rect.x + rect.w - 1;
  const bottom = tile.y <= rect.y + rect.h - 1;
  return left && right && top && bottom;
}

export function showRoom(map, room) {
  setRoomAlpha(map, room, 1);
  room.forEach(rect => {
    // ground tiles
    map.forEachTile(
      tile => {
        tile.index = -1;
      },
      this,
      rect.x,
      rect.y,
      rect.w,
      rect.h,
      null,
      'shade_layer'
    );
  });
}
export function shadeRoom(map, room) {
  setRoomAlpha(map, room, 1);
  room.forEach(rect => {
    // ground tiles
    map.forEachTile(
      tile => {
        tile.index = SHADOW;
      },
      this,
      rect.x,
      rect.y,
      rect.w,
      rect.h,
      null,
      'shade_layer'
    );
  });
}
export function hideRoom(map, room) {
  setRoomAlpha(map, room, 0);
  room.forEach(rect => {
    // ground tiles
    map.forEachTile(
      tile => {
        tile.index = -1;
      },
      this,
      rect.x,
      rect.y,
      rect.w,
      rect.h,
      null,
      'shade_layer'
    );
  });
}
export function setRoomAlpha(map, room, alpha) {
  room.forEach(rect => {
    // ground tiles
    [
      'ground_layer',
      'below_player',
      'decor_layer',
      'above_player',
      'logic_layer'
    ].forEach(layerName => {
      map.forEachTile(
        tile => {
          tile.setAlpha(alpha);
          if (tile.occupant) {
            if (tile.occupant.parentContainer) {
              tile.occupant.parentContainer.setAlpha(alpha);
            } else {
              tile.occupant.setAlpha(alpha);
            }
          }
          if (tile.torch) {
            tile.torch.setAlpha(alpha);
          }
        },
        this,
        rect.x,
        rect.y,
        rect.w,
        rect.h,
        null,
        layerName
      );
    });
  });
}

export function getRoomsAt(x, y, rooms) {
  let inRooms = [];
  rooms.forEach((room, roomIndex) => {
    room.forEach(rect => {
      if (x >= rect.x && x < rect.x + rect.w) {
        if (y >= rect.y && y < rect.y + rect.h) {
          inRooms.push(roomIndex);
        }
      }
    });
  });
  return [...new Set(inRooms)];
}

export function checkerBoard(map, levelData, unchecker = false) {
  let modification = unchecker ? -1 : 1;
  levelData.forEach((row, colIndex) => {
    row.forEach((tileKey, rowIndex) => {
      let y = colIndex;
      let x = rowIndex;

      if (tileKey !== -1) {
        let tile = tileKey;
        if (tileShouldBeCheckered(rowIndex, colIndex)) {
          tile += modification;
        }
        map.putTileAt(tile, x, y, true, 'ground_layer');
      }
    });
  });
}

export function tileShouldBeCheckered(x, y) {
  return (x % 2 !== 0 && y % 2 !== 0) || (x % 2 === 0 && y % 2 === 0);
}

export function isLegalGroundTile(index) {
  let x = index % 18;
  let y = Math.floor(index / 18);
  if (y < 12 && x < 8) {
    return true;
  }
  return false;
}

export function isGroundTileDarkIndex(index) {
  let isDark = false;
  let x = index % 18;
  let isGround = isLegalGroundTile(index);
  if (isGround) {
    if (x % 2 === 1) {
      isDark = true;
    }
  }
  return isDark;
}

export function isGroundTileFirstTile(index) {
  let isFirst = false;
  let x = index % 18;
  let isGround = isLegalGroundTile(index);
  if (isGround) {
    if (x === 0) {
      isFirst = true;
    }
  }
  return isFirst;
}

export function isLegalLogicTile(index) {
  const logicTiles = [LOGIC_DOOR, UNWALKABLE, WALKABLE, WARP, KEY, LOCKED_DOOR];
  if (RED_PIECES.concat(BLUE_PIECES, ITEMS, logicTiles).includes(index + 1)) {
    if ([283, 284].includes(index)) {
      return false;
    }
    return true;
  }
  return false;
}

export function isLegalDecorativeTile(index) {
  let x = index % 18;
  let y = Math.floor(index / 18);
  if (x > 7 && y < 12) {
    return true;
  }
  const partialZone = [32, 33, 34].includes(y) && x < 14;
  if (y > 17 && !partialZone) {
    if (ITEMS.includes(index + 1)) {
      return false;
    }
    return true;
  }
}

export function distributeRandomEnemies(
  scene,
  levelData,
  origin = { x: 0, y: 0 }
) {
  const { party } = loadProgress();
  let highestRank = -1;
  party.forEach(member => {
    if (member.rank > highestRank) {
      highestRank = member.rank;
    }
  });
  let limit = highestRank + 1;
  while (limit) {
    const randX = rand(origin.x, origin.x + levelData.logic[0].length - 1);
    const randY = rand(origin.y, origin.y + levelData.logic.length - 1);
    const logicTile = scene.map.getTileAt(randX, randY, true, 'logic_layer');
    const empty = logicTile.index === -1;
    if (empty) {
      limit -= 1;
      const randRank = 1; // rand(0, highestRank);
      const pieceIndex = randRank + RED_PIECES[0];
      GameplayHelpers.addPieceSpriteToBoard(
        scene,
        logicTile,
        null,
        pieceIndex,
        false
      );
    }
  }
}

const baseSpriteIndexByColor = {
  red: 307,
  blue: 271,
  colorless: 235
};

export function addFakePieceToWorld(scene, x, y, color, rank, logic = true) {
  const startingIndex = baseSpriteIndexByColor[color];
  let tile;
  if (typeof rank === 'number') {
    tile = startingIndex + rank;
  } else {
    tile = startingIndex + PIECE_TYPES.indexOf(rank);
  }
  scene.map.putTileAt(tile - 18, x, y - 1, true, 'above_player');
  scene.map.putTileAt(tile, x, y, true, 'below_player');
  if (logic) {
    scene.map.putTileAt(UNWALKABLE, x, y, true, 'logic_layer');
  }
}
export function removeFakePieceFromWorld(scene, x, y, logic = true) {
  scene.map.removeTileAt(x, y - 1, false, true, 'above_player');
  scene.map.removeTileAt(x, y, false, true, 'below_player');
  if (logic) {
    scene.map.removeTileAt(x, y, false, true, 'logic_layer');
  }
}
